import { createSelector } from '@reduxjs/toolkit';
import {
    DISMISS_MODAL,
    OPEN_REGISTER_FOR_CATALOG_MODAL,
    REGISTER_FOR_CATALOG_FAIL,
    REGISTER_FOR_CATALOG_REQUEST,
    REGISTER_FOR_CATALOG_SUCCESS,
} from './actions';
import { fetchBidLimitBalanceIfNeeded } from './bidLimit';
import { getAuthToken, getBidderId } from './user';
import { getDeployment } from './config';
import { getRequestedAuctionRegistrationAnalytics, trackConversionForAlgolia } from './analytics';
import { GlobalState } from '../rootReducer';
import { postRegisterForCatalog } from '@/redux/modules/catalog/catalog.api';

/* reducer */
export const DEFAULT_STATE = {
    catalogId: 0,
    error: undefined,
    itemId: 0,
    submitted: false,
    success: false,
};

export type State = typeof DEFAULT_STATE;

export default function reducer(state: State = DEFAULT_STATE, action: any = {}) {
    switch (action.type) {
        case OPEN_REGISTER_FOR_CATALOG_MODAL:
            return {
                ...state,
                catalogId: action.payload.catalogId,
                itemId: action.payload.itemId ? action.payload.itemId : 0,
            };
        case REGISTER_FOR_CATALOG_FAIL:
            return {
                ...state,
                error: action.payload,
                submitted: false,
                success: false,
            };
        case REGISTER_FOR_CATALOG_REQUEST:
            return {
                ...state,
                submitted: true,
                success: false,
            };
        case REGISTER_FOR_CATALOG_SUCCESS:
            return {
                ...state,
                submitted: false,
                success: true,
            };

        case DISMISS_MODAL:
            return DEFAULT_STATE;
        default:
            return state;
    }
}

/* SELECTORS */
const registerForCatalogSelector = (state: GlobalState) => state.registerForCatalog;

export const getUserRegisterForCatalog = createSelector(
    registerForCatalogSelector,
    (catalogRegistrations) => catalogRegistrations
);

export const getRegisterForAuctionSubmitted = createSelector(registerForCatalogSelector, (slice) => slice.submitted);
export const getRegisterForAuctionSuccess = createSelector(registerForCatalogSelector, (slice) => slice.success);
export const getRegisterForAuctionCatalogId = createSelector(registerForCatalogSelector, (slice) => slice.catalogId);
export const getRegisterForAuctionItemId = createSelector(registerForCatalogSelector, (slice) => slice.itemId);

/* ACTION CREATORS */
type SubmitRegisterForCatalogParams = {
    catalogId: number;
    itemId: number;
};

export const submitRegisterForCatalog =
    ({ catalogId, itemId }: SubmitRegisterForCatalogParams) =>
    async (dispatch: Function, getState: Function) => {
        try {
            const state = getState();
            const authToken = getAuthToken(state);
            const bidderId = getBidderId(state);
            const deployment = getDeployment(state);

            dispatch({ type: REGISTER_FOR_CATALOG_REQUEST });

            const response = await postRegisterForCatalog({ authToken, bidderId, catalogId, deployment, itemId });
            const analytics = getRequestedAuctionRegistrationAnalytics(state, catalogId, itemId);
            dispatch(trackConversionForAlgolia(itemId));
            await dispatch({
                meta: { analytics: analytics.analytics },
                payload: response.payload,
                type: REGISTER_FOR_CATALOG_SUCCESS,
            });
            dispatch(fetchBidLimitBalanceIfNeeded({ catalogId }));
        } catch (error) {
            dispatch({
                error: true,
                meta: { catalogId },
                payload: error,
                type: REGISTER_FOR_CATALOG_FAIL,
            });
        }
    };
